import { Component, useContext } from 'react'
import type { ComponentType, ReactNode } from 'react'
import type { ParseKeys, TOptions, i18n } from 'i18next'
import { ReactReduxContext, useSelector } from 'react-redux'
import hoistStatics from 'hoist-non-react-statics'

import { i18nContext } from '../components/withI18n'
import { i18nInstancesContext } from '../components/I18nProvider'

/** Creates "t" function which uses `i18next.t` */
export function createT(i18next: i18n, isBeyondShop = false): TranslateProps['t'] {
  return function (key: ParseKeys, options?: TOptions) {
    const translationKey = isBeyondShop && i18next.exists(`${key}_BYD`) ? `${key}_BYD` : key

    // If the key does not exist in our translations we just return the provided text.
    //
    // These are most likely values from the API that can be either an existing translation
    // or an individual text from the merchant which does not get a translation.
    // An example for this is the stock-level availabilityText.
    //
    // Setting `count` is needed so that pluralized locales are found with the i18next
    // key lookup (they have a plural key suffix like "_one" or "_other").
    // See: https://www.i18next.com/translation-function/plurals
    if (!i18next?.exists(translationKey, { count: 1 })) return translationKey

    return i18next.t(translationKey, options)
  }
}

/** Translate with components as placeholder values */
export function withComponents(t: TranslateProps['t']) {
  return (translationKey: ParseKeys, components: Record<string, ReactNode>) => {
    // Make sure the translation is not interpolated since we need the placeholders for component injection
    const nonInterpolatedTranslation: string = t(translationKey, { interpolation: { suffix: '__', prefix: '__' } })
    // Get everything in between the placeholders for interspersal
    const intersperseTranslations = nonInterpolatedTranslation.split(/{{\w+}}/g)
    // Get the placeholders
    const placeholders: string[] = nonInterpolatedTranslation.match(/{{(\w)+}}/g) || []

    return placeholders.reduce<ReactNode>(
      (translationPart, placeholder, index) => (
        <>
          {translationPart || null}
          {components[placeholder.match(/\w+/)?.[0] || '']}
          {intersperseTranslations[index + 1] || null}
        </>
      ),
      intersperseTranslations[0],
    )
  }
}

/** Creates "useTranslate" hook to use with i18next and Phrase. */
export function useTranslate(i18nType: 'shop' | 'interface', scope?: string) {
  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const i18nInstanceForType = useContext(i18nInstancesContext)[`${i18nType}I18n`]!

  const unscopedT = createT(i18nInstanceForType, isBeyondShop)

  return scope
    ? // allow scoping by passing scope parameter
      (key: ParseKeys, opts?: TOptions) =>
        key.startsWith(':')
          ? // allow breaking out of the scope by using a colon prefix
            unscopedT(key.substring(1), opts)
          : unscopedT(`${scope}.${key}`, opts)
    : unscopedT
}

/**
 * Creates "translate" higher-order component to use with i18next and Phrase.
 *
 * @deprecated Use `useTranslate` instead.
 */
export default function translate(scope?: string) {
  return function withTranslation<P>(WrappedComponent: ComponentType<P>) {
    class Translate extends Component<Omit<P, keyof TranslateProps>> {
      static displayName = WrappedComponent.displayName || 'Component'

      render() {
        // Accessing the store here directly because using connect (from redux) leads to issues with rerendering
        // These issues occur because we are using the legacy context api: https://reactjs.org/docs/legacy-context.html#updating-context
        return (
          <i18nContext.Consumer>
            {(i18n: i18n) => (
              <ReactReduxContext.Consumer>
                {({ store }) => {
                  const isBeyond = Boolean(store.getState().getIn(['shop', 'beyond']))
                  const unscopedT = createT(i18n, isBeyond)
                  const t = scope
                    ? // allow scoping by passing scope parameter
                      (key: ParseKeys, opts?: TOptions) =>
                        key.startsWith(':')
                          ? // allow breaking out of the scope by using a colon prefix
                            unscopedT(key.substring(1), opts)
                          : unscopedT(`${scope}.${key}`, opts)
                    : unscopedT
                  return <WrappedComponent {...(this.props as P)} t={t} />
                }}
              </ReactReduxContext.Consumer>
            )}
          </i18nContext.Consumer>
        )
      }
    }

    return hoistStatics(Translate, WrappedComponent)
  }
}
